import React, {useMemo, useState} from 'react'
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Collapse,
    FormControl,
    FormControlLabel,
    FormLabel,
    MenuItem,
    Radio,
    RadioGroup,
    Select,
    Stack,
    TextField,
    Typography,
} from "@mui/material"
import PlayArrowIcon from "@mui/icons-material/PlayArrow"
import {ProjectJson} from "../../api/json/ProjectJson"
import AsyncButton from "../button/AsyncButton"
import ApiError from "../../api/ApiError"
import ErrorMessage from "../error/ErrorMessage"
import CompletionOptionsForm from "../completions/CompletionOptionsForm"
import GroupedModelSelect from "../models/GroupedModelSelect"
import {AnyModelSpecJson} from "../../api/json/ModelSpecJson"
import {CompletionOptionsJson, DEFAULT_COMPLETION_OPTIONS} from "../../api/json/CompletionOptionsJson"
import TransformJson from "../../api/json/TransformJson"
import SpaceBetween from "../common/SpaceBetween"
import {TransformJobRequests, TransformJobDestination} from "../../api/requests/TransformJobRequests"
import TransformJobJson from "../../api/json/TransformJobJson"
import {ColumnType} from "../../api/enum/ColumnType"

interface TransformJobFormProps {
    project: ProjectJson
    transform: TransformJson
    onCancel: () => void
    onStarted: (job: TransformJobJson) => void
}

function deriveReference(name: string): string {
    return name.toLowerCase().replace(/[^a-zA-Z0-9]+/g, "_").replace(/^_+|_+$/g, "")
}

function uniqueReference(project: ProjectJson, base: string): string {
    if (!base) return base
    const existing = new Set(project.columns.map(f => f.reference))
    if (!existing.has(base)) return base
    let i = 2
    while (existing.has(`${base}_${i}`)) i++
    return `${base}_${i}`
}

function TransformJobForm({project, transform, onCancel, onStarted}: TransformJobFormProps) {
    const [destinationKind, setDestinationKind] = useState<'new' | 'existing'>('new')

    const [fieldName, setFieldName] = useState<string>(transform.name)

    const [existingColumnId, setExistingFieldId] = useState<string>("")
    const [overwriteConfirmed, setOverwriteConfirmed] = useState(false)

    const [model, setModel] = useState<AnyModelSpecJson>()
    const [completionOptions, setCompletionOptions] = useState<CompletionOptionsJson>(DEFAULT_COMPLETION_OPTIONS)

    const [advancedOpen, setAdvancedOpen] = useState(false)

    const [saving, setSaving] = useState(false)
    const [error, setError] = useState<ApiError>()

    const textFields = useMemo(
        () => project.columns.filter(f => f.type === ColumnType.TEXT),
        [project.columns]
    )

    const selectedExistingField = useMemo(
        () => textFields.find(f => f.id === existingColumnId),
        [textFields, existingColumnId]
    )

    const existingFieldHasValues = (selectedExistingField?.stats?.numberOfValues ?? 0) > 0

    const derivedReference = useMemo(
        () => uniqueReference(project, deriveReference(fieldName)),
        [project, fieldName]
    )

    const destinationValid = destinationKind === 'new'
        ? fieldName.trim().length > 0 && derivedReference.length > 0
        : !!existingColumnId && overwriteConfirmed

    const disabled = !destinationValid || !model || !completionOptions

    const handleCreate = () => {
        if (!model || !completionOptions) return

        const destination: TransformJobDestination = destinationKind === 'new'
            ? {kind: 'new', name: fieldName.trim(), reference: derivedReference}
            : {kind: 'existing', fieldId: existingColumnId}

        setSaving(true)
        setError(undefined)
        TransformJobRequests.create(project.id, transform.id, destination, model, completionOptions)
            .then(job => onStarted(job))
            .catch(setError)
            .finally(() => setSaving(false))
    }

    return (
        <Stack spacing={3}>
            <FormControl>
                <FormLabel sx={{mb: 1, fontWeight: 500}}>Destination</FormLabel>
                <RadioGroup value={destinationKind}
                            onChange={(e) => setDestinationKind(e.target.value as 'new' | 'existing')}>
                    <FormControlLabel value="new" control={<Radio/>} label="Create a new column"/>
                    {destinationKind === 'new' &&
                        <Box sx={{pl: 4, pt: 1, pb: 1}}>
                            <TextField label="Column name"
                                       size="small"
                                       value={fieldName}
                                       onChange={(e) => setFieldName(e.target.value)}
                                       fullWidth/>
                        </Box>
                    }
                    <FormControlLabel value="existing" control={<Radio/>} label="Target an existing column"/>
                    {destinationKind === 'existing' &&
                        <Box sx={{pl: 4, pt: 1, pb: 1}}>
                            <Stack spacing={1}>
                                {textFields.length === 0 ?
                                    <Typography variant="body2" color="text.secondary">
                                        This project has no text columns to target. Create a new column instead.
                                    </Typography>
                                    :
                                    <Select value={existingColumnId}
                                            onChange={(e) => {
                                                setExistingFieldId(e.target.value)
                                                setOverwriteConfirmed(false)
                                            }}
                                            displayEmpty
                                            size="small">
                                        <MenuItem value="" disabled>
                                            <em>Pick a column</em>
                                        </MenuItem>
                                        {textFields.map(f =>
                                            <MenuItem key={f.id} value={f.id}>
                                                {f.name}
                                            </MenuItem>
                                        )}
                                    </Select>
                                }
                                {selectedExistingField &&
                                    <Alert severity="warning" sx={{mt: 1}}>
                                        <Typography variant="body2" sx={{fontWeight: 500, mb: 0.5}}>
                                            Warning: This will overwrite all values in the column. This is irreversible.
                                        </Typography>
                                        {existingFieldHasValues &&
                                            <Typography variant="caption" color="text.secondary" sx={{display: 'block', mb: 1}}>
                                                {selectedExistingField.stats?.numberOfValues?.toLocaleString()} existing values in "{selectedExistingField.name}" will be replaced.
                                            </Typography>
                                        }
                                        <FormControlLabel
                                            control={
                                                <Checkbox checked={overwriteConfirmed}
                                                          size="small"
                                                          onChange={(e) => setOverwriteConfirmed(e.target.checked)}/>
                                            }
                                            label={<Typography variant="body2">I understand, overwrite the column</Typography>}
                                        />
                                    </Alert>
                                }
                            </Stack>
                        </Box>
                    }
                </RadioGroup>
            </FormControl>

            <GroupedModelSelect value={model} onChange={setModel} instructModelsOnly label="Platform & Base Model"/>

            {model &&
                <Box>
                    <Button size="small"
                            onClick={() => setAdvancedOpen(o => !o)}
                            sx={{alignSelf: "flex-start", pl: 0}}>
                        {advancedOpen ? "Hide advanced options" : "Show advanced options"}
                    </Button>
                    <Collapse in={advancedOpen} unmountOnExit={false}>
                        <Box sx={{pt: 2}}>
                            <CompletionOptionsForm platform={model.platform}
                                                   value={completionOptions}
                                                   hideNumberOfCompletions
                                                   hideAdvanced
                                                   onChange={setCompletionOptions}/>
                        </Box>
                    </Collapse>
                </Box>
            }

            {error && <ErrorMessage error={error}/>}

            <SpaceBetween>
                <Button onClick={onCancel}>Cancel</Button>
                <AsyncButton variant="contained"
                             startIcon={<PlayArrowIcon/>}
                             disabled={disabled}
                             waiting={saving}
                             onClick={handleCreate}>
                    {`Run on ${project.stats.totalRows.toLocaleString()} ${project.stats.totalRows === 1 ? "row" : "rows"}`}
                </AsyncButton>
            </SpaceBetween>
        </Stack>
    )
}

export default TransformJobForm
